import React from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Tag, Clock, Layers } from 'lucide-react'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

const CompanyActivityCard = ({ annonce }) => {
  const { t, i18n } = useTranslation()
  const dk = useSearchTokens()

  if (!annonce) return null

  const lang = i18n.language?.split('-')[0]
  const getLocalizedText = (textObj) => {
    if (!textObj) return null
    if (typeof textObj === 'string') return textObj
    return textObj[lang] || textObj.en || textObj.fr || textObj.ar || null
  }

  const classification = annonce.classification_activite || {}
  const secteur = classification.secteur_principal || annonce.secteur_principal
  const secteursSecondaires = classification.secteurs_secondaires || []
  const keywords = classification.mots_cles_activite || []
  const duree = annonce.duree
  const nombreParts = annonce.nombre_parts_total
  const valeurPart = annonce.valeur_nominale_part

  const hasContent = secteur || keywords.length > 0 || duree || nombreParts
  if (!hasContent) return null

  const infoItems = [
    duree != null && {
      icon: <Clock size={13} />,
      label: t('duree_societe', 'Durée'),
      value: `${duree} ${t('ans', 'ans')}`,
    },
    nombreParts != null && {
      icon: <Layers size={13} />,
      label: t('parts_sociales', 'Parts sociales'),
      value: valeurPart
        ? `${Number(nombreParts).toLocaleString('fr-FR')} × ${Number(valeurPart).toLocaleString('fr-FR')} MAD`
        : Number(nombreParts).toLocaleString('fr-FR'),
    },
  ].filter(Boolean)

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: '12px',
        border: `1px solid ${dk.border}`,
        backgroundColor: dk.surface,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tag size={16} color={dk.gold} />
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            color: dk.text1,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {t('activite', 'Activité')}
        </Typography>
      </Box>

      {/* Sectors row */}
      {(secteur || secteursSecondaires.length > 0) && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: infoItems.length > 0 || keywords.length > 0 ? 2 : 0 }}>
          {secteur && (
            <Chip
              label={t(`secteur_${secteur}`, secteur.replace(/_/g, ' '))}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: dk.gold,
                backgroundColor: dk.goldSub,
                border: `1px solid ${dk.goldBdr}`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          {secteursSecondaires.map((s, i) => (
            <Chip
              key={i}
              label={t(`secteur_${s}`, s.replace(/_/g, ' '))}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 500,
                color: dk.text2,
                backgroundColor: dk.chipBg,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          ))}
        </Box>
      )}

      {/* Info items (duration, shares) */}
      {infoItems.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: `repeat(${infoItems.length}, 1fr)` },
            gap: 1.5,
            mb: keywords.length > 0 ? 2 : 0,
          }}
        >
          {infoItems.map(({ icon, label, value }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ color: dk.text3, display: 'flex', mt: '3px', flexShrink: 0 }}>{icon}</Box>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                  {value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Keywords */}
      {keywords.length > 0 && (
        <Box sx={{ pt: (secteur || infoItems.length > 0) ? 2 : 0, borderTop: (secteur || infoItems.length > 0) ? `1px solid ${dk.border}` : 'none' }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
            {t('mots_cles', 'Mots-clés')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
            {keywords.map((kw, idx) => (
              <Chip
                key={idx}
                label={getLocalizedText(kw)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 500,
                  color: dk.text2,
                  backgroundColor: dk.elevated,
                  border: `1px solid ${dk.borderLight}`,
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CompanyActivityCard
